import { z } from 'zod'
import { tolerantArray } from '@/schemas/shared/tolerantArray'
import { MoneySchema } from '../shared/money'

/**
 * Schemas dos pedidos do cliente (meus pedidos, detalhe em /pedido/[orderNumber] e o novo link de pagamento).
 * O checkout importa daqui o status e o detalhe do pedido.
 *
 * Contrato da API (Glofi.API/Controllers/OrderController.cs e CustomerController.UpdatePaymentMethod), travado em
 * tests/Glofi.API.Tests/Snapshots/Orders/{list,detail}.shape.txt:
 *
 * | Rota | Verbo | Corpo | Resposta 200 |
 * | --- | --- | --- | --- |
 * | `order/list?page=0&rows=30` | GET | - | `IOrderSummary[]` (página de 30 por padrão) |
 * | `order/{orderNumber}` | GET | - | `IOrderDetail`, ou o 200 com BadRequest embrulhado (pedido de outro cliente) |
 * | `customer/update-payment-link` | POST | `{ orderNumber, paymentType }` | `{ order, message }` |
 *
 * Valores em dinheiro: `decimal` sai como número no JSON, mas pedidos antigos têm o valor gravado como texto no Mongo;
 * o schema aceita os dois e devolve número.
 */

/** Status do pedido como a API serializa (EnumMember de Glofi.Data/Enum/OrderStatus.cs). */
export const ORDER_STATUS = {
    NONE: ' ',
    NEW: 'New',
    CANCELLED: 'Cancelled',
    PAID: 'Paid',
    WAITING_PAYMENT: 'WaitingPayment',
    WAITING_DELIVERY: 'WaitingDelivery',
    PRODUCT_SEPARATION: 'ProductSeparation',
    WAITING_NF_EMISSION: 'WaitingNFEmission',
    BILLED: 'Billed',
    READY_TO_SHIP: 'ReadyToShip',
    ON_CARRIAGE: 'OnCarriage',
    DELIVERED: 'Delivered',
    IN_PROGRESS: 'InProgress',
    PROCESSING_PAYMENT: 'ProcessingPayment',
} as const

export type IOrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS]

/**
 * Status aceito como texto livre: um status novo no backend não pode derrubar a tela de pedidos. Compare com
 * `ORDER_STATUS`.
 */
const OrderStatusSchema = z.string()

const nullableText = z.string().nullish()

// ===== LISTA =====

/** Item de `order/list`. Não tem `id`: a chave da lista é o `orderNumber`. */
export const OrderSummarySchema = z.object({
    orderNumber: z.string(),
    createDate: z.string(),
    lastUpdateDate: z.string().nullish(),
    total: MoneySchema,
    status: OrderStatusSchema,
})

/** Pedido ilegível sai da lista (e é registrado); os outros aparecem. */
export const OrderListSchema = tolerantArray(OrderSummarySchema, 'pedidos do cliente')

// ===== DETALHE =====

/** Endereço de entrega copiado no pedido (sem id, nome, bairro nem principal). */
export const OrderShippingAddressInfoSchema = z.object({
    street: nullableText,
    number: nullableText,
    complement: nullableText,
    city: nullableText,
    zipCode: nullableText,
    state: nullableText,
    country: nullableText,
})

export const OrderPaymentTransactionSchema = z.object({
    /** Titular do cartão (vazio depois do mascaramento) ou null no PIX. */
    nome: nullableText,
    /** Copia e cola do PIX (é o valor do QR Code); null no cartão. */
    pixKey: nullableText,
    valor: MoneySchema.nullish(),
    /** Cartão mascarado (só os 4 últimos dígitos); null no PIX. */
    numeroCartao: nullableText,
})

export const OrderCartItemSchema = z.object({
    quantity: z.number(),
    product: z.object({
        discountedPrice: MoneySchema.nullish(),
        imagesURL: z
            .array(z.string())
            .nullish()
            .transform((value) => value ?? []),
        name: z.string(),
        sellerName: nullableText,
    }),
})

export const OrderDetailSchema = z.object({
    /** Data em que o link de pagamento (PIX) foi criado; null enquanto o pedido não teve tentativa de pagamento. */
    linkCreatedDate: z.string().nullish(),
    orderNumber: z.string(),
    createDate: z.string(),
    lastUpdateDate: z.string().nullish(),
    shippingAddress: z.object({
        addressInfo: OrderShippingAddressInfoSchema.nullish(),
    }),
    total: MoneySchema,
    /** `name` é o texto exibido e o que a loja compara ("PIX", "Cartão de Crédito"). */
    paymentMethod: z.object({ name: z.string() }).nullish(),
    paymentPolicyRule: z.object({ name: nullableText }).nullish(),
    paymentTransaction: z
        .array(OrderPaymentTransactionSchema)
        .nullish()
        .transform((value) => value ?? []),
    status: OrderStatusSchema,
    cart: z.object({
        subTotal: MoneySchema,
        shippingTax: MoneySchema,
        cartItems: z.array(OrderCartItemSchema),
        discountApplied: z.object({ name: nullableText, discountCode: nullableText }).nullish(),
    }),
})

/**
 * Pedido de outro cliente: a API responde 200 com o BadRequest serializado
 * (`new JsonResult(BadRequest(new { message = "Pedido não encontrado!" }))`, travado em
 * Orders/OrderTests.Comportamento_atual_pedido_de_outro_cliente_responde_200_com_mensagem_de_nao_encontrado).
 */
export const ForeignOrderEnvelopeSchema = z.object({
    value: z.object({ message: z.string() }),
    statusCode: z.number(),
})

/** Resposta de `order/{orderNumber}`: o pedido ou o envelope de "não encontrado". O service separa os dois. */
export const OrderDetailResponseSchema = z.union([OrderDetailSchema, ForeignOrderEnvelopeSchema])

// ===== NOVO LINK DE PAGAMENTO =====

/** Tipo de pagamento como a API aceita no enum `PaymentType` (só o PIX tem link refeito pela loja hoje). */
export const PAYMENT_TYPE = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'CreditCard',
} as const

export const UpdatePaymentLinkRequestSchema = z.object({
    orderNumber: z.string().min(1),
    paymentType: z.enum([PAYMENT_TYPE.PIX, PAYMENT_TYPE.BOLETO, PAYMENT_TYPE.CREDIT_CARD]),
})

/**
 * `{ order, message }`: o pedido no formato do detalhe, sem `linkCreatedDate`.
 * Erros: 404/409 `{ statusError: "orderError", message }`; 400 `{ messsage }` (três "s") sem número do pedido;
 * 400 `{ message, error }` quando o pagamento é recusado.
 */
export const UpdatePaymentLinkResponseSchema = z.object({
    order: OrderDetailSchema,
    message: z.string().nullish(),
})

// ===== TIPOS =====

export type IOrderSummary = z.infer<typeof OrderSummarySchema>
export type IOrderDetail = z.infer<typeof OrderDetailSchema>
export type IOrderCartItem = z.infer<typeof OrderCartItemSchema>
export type IOrderPaymentTransaction = z.infer<typeof OrderPaymentTransactionSchema>
export type IOrderShippingAddressInfo = z.infer<typeof OrderShippingAddressInfoSchema>
export type IOrderDetailResponse = z.infer<typeof OrderDetailResponseSchema>
export type IPaymentType = z.infer<typeof UpdatePaymentLinkRequestSchema>['paymentType']
export type IUpdatePaymentLinkRequest = z.infer<typeof UpdatePaymentLinkRequestSchema>
export type IUpdatePaymentLinkResponse = z.infer<typeof UpdatePaymentLinkResponseSchema>
